// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * API endpoint for removing files associated with assets.
 * Provides functionality to delete documents, images, or other files
 * that have been previously attached to assets.
 */
import * as z from 'zod';
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';

/**
 * Schema for validating the API response when deleting an asset file.
 * Transforms the response to provide a more intuitive structure with
 * the success flag separated from metadata.
 */
const deleteTicketFileResponseSchema = z
  .object({
    data: z.object({ success: z.boolean() }),
    metadata: responseMetadataSchema,
  })
  .transform(({ data, ...response }) => ({
    ...response,
    success: data.success,
  }));

/**
 * Response type for file deletion operations.
 * @property {boolean} success - Whether the deletion was successful
 * @property {string} requestId - Unique identifier for the request
 */
type DeleteTicketFileResponse = z.infer<typeof deleteTicketFileResponseSchema>;

/**
 * Deletes an ticket file associated with a specific ticket.
 *
 * @param {string} ticketKey - The unique identifier for the ticket.
 * @param {string} fileKey - The unique identifier for the file to delete.
 * @return {Promise<DeleteTicketFileResponse>} A promise that resolves to the response of the delete operation.
 */
export async function deleteTicketFile(
  ticketKey: string,
  fileKey: string
): Promise<DeleteTicketFileResponse> {
  const url = `/v1/ticket/${ticketKey}/file/${fileKey}`;
  const response = await api.delete(url);

  return parseResponse(deleteTicketFileResponseSchema, response.data, {
    schemaName: 'DeleteTicketFile',
    endpoint: url,
  });
}
